import { createEntityAdapter, EntityState, Update } from '@ngrx/entity';
import { createFeature, createReducer, on } from '@ngrx/store';
import { OrderData, Quote } from '@zero/shared/util-models';
import { orderActions, orderApiActions } from './order.actions';

const entityOptions = {
  selectId: (order: OrderData) => order.orderId,
};
export const openOrdersAdapter = createEntityAdapter<OrderData>(entityOptions);
export const filledOrdersAdapter = createEntityAdapter<OrderData>(entityOptions);
export const canceledOrdersAdapter = createEntityAdapter<OrderData>(entityOptions);

export interface OrdersStateField extends EntityState<OrderData> {
  loading: boolean;
  initiallyLoaded: boolean;
  page: number;
  hasNext: boolean;
}

const DEFAULT_ORDER_FIELD_STATE = {
  hasNext: false,
  loading: false,
  initiallyLoaded: false,
  page: 0,
};

export interface OrderState {
  openOrders: OrdersStateField;
  filledOrders: OrdersStateField;
  canceledOrders: OrdersStateField;
}

export const initialState: OrderState = {
  openOrders: openOrdersAdapter.getInitialState({ ...DEFAULT_ORDER_FIELD_STATE }),
  filledOrders: filledOrdersAdapter.getInitialState({ ...DEFAULT_ORDER_FIELD_STATE }),
  canceledOrders: canceledOrdersAdapter.getInitialState({ ...DEFAULT_ORDER_FIELD_STATE }),
};

const stateFieldDictionary = {
  openOrders: {
    adapter: openOrdersAdapter,
    initialState: openOrdersAdapter.getInitialState({ ...DEFAULT_ORDER_FIELD_STATE }),
  },
  filledOrders: {
    adapter: filledOrdersAdapter,
    initialState: filledOrdersAdapter.getInitialState({ ...DEFAULT_ORDER_FIELD_STATE }),
  },
  canceledOrders: {
    adapter: canceledOrdersAdapter,
    initialState: canceledOrdersAdapter.getInitialState({ ...DEFAULT_ORDER_FIELD_STATE }),
  },
};

export const orderStateFeatureKey = 'order';

export const orderFeature = createFeature({
  name: orderStateFeatureKey,
  reducer: createReducer(
    initialState,
    // load orders
    on(orderActions.setLoading, (state, { category, loading }): OrderState => {
      const stateField = state[category];
      return {
        ...state,
        [category]: {
          ...stateField,
          loading,
        },
      };
    }),
    // load order success
    on(orderApiActions.loadOrdersSuccess, (state, { category, orders, hasNext }): OrderState => {
      const stateField = state[category];
      return {
        ...state,
        [category]: {
          ...stateField,
          ...stateFieldDictionary[category].adapter.addMany(orders, stateField),
          loading: false,
          initiallyLoaded: true,
          hasNext,
        },
      };
    }),
    // load order failure
    on(orderApiActions.loadOrdersFailure, (state, { category }): OrderState => {
      const stateField = state[category];
      return {
        ...state,
        [category]: {
          ...stateField,
          loading: false,
        },
      };
    }),
    // reset order state
    on(
      orderActions.resetOrder,
      (state, { category }): OrderState => ({
        ...state,
        [category]: stateFieldDictionary[category].initialState,
      }),
    ),
    // increment page
    on(orderActions.incrementOrderPage, (state, { category }): OrderState => {
      const stateField = state[category];
      return {
        ...state,
        [category]: {
          ...stateField,
          page: stateField.page + 1,
        },
      };
    }),
    // update open orders quotes
    on(orderActions.updateOpenOrdersQuotes, (state, quotes) => {
      if (!quotes) {
        return state;
      }

      const stateField = state.openOrders;
      const entities = openOrdersAdapter.getSelectors().selectAll(stateField);

      const newOrdersList = Object.values(quotes)
        .flatMap((quote: Quote) => {
          if (!quote.bid) {
            return [];
          }

          // Get all orders by isin
          const ordersByIsin = entities.filter((order: OrderData) => order.isin === quote.isin);

          // Map the orders to updates
          return ordersByIsin.map((order: OrderData) => {
            if (quote.time === order.orderQuoteTime || !quote.bid) {
              return { id: order.id, changes: {} } as Update<OrderData>;
            }

            const quoteByDirection = order.direction === 'buy' ? quote.ask : quote.bid;

            let calculatedOpenQuantity = order.openQuantity;

            const { fixedRemainingAmount, execType, direction } = order;
            const isFractionBuyOrderWithFixedRemainingAmount =
              execType === 'fraction' && direction === 'buy' && !Number.isNaN(fixedRemainingAmount) && fixedRemainingAmount > 0;
            if (isFractionBuyOrderWithFixedRemainingAmount) {
              calculatedOpenQuantity = order.fixedRemainingAmount / quoteByDirection;
            }

            return {
              id: order.orderId,
              changes: {
                orderQuoteTime: quote.time,
                orderQuote: quoteByDirection,
                quote: quoteByDirection,
                openQuantity: calculatedOpenQuantity,
              },
            } as Update<OrderData>;
          });
        })
        .filter((order: Update<OrderData>) => order);

      return {
        ...state,
        openOrders: {
          ...stateField,
          ...openOrdersAdapter.updateMany(newOrdersList, stateField),
        },
      };
    }),
    // update open order stop price
    on(orderActions.updateOpenOrderStopPrice, (state, { orderId, stopPrice }) => {
      const stateField = state.openOrders;
      return {
        ...state,
        openOrders: {
          ...stateField,
          ...openOrdersAdapter.updateOne(
            {
              id: orderId,
              changes: {
                stopPrice,
              },
            },
            stateField,
          ),
        },
      };
    }),
    // set loading
    on(orderActions.setLoading, (state, { category, loading }): OrderState => {
      const stateField = state[category];
      return {
        ...state,
        [category]: {
          ...stateField,
          loading,
        },
      };
    }),
  ),
});
